// src/pages/hooks/useSessaoCompleta.js

import React, { useMemo, useCallback, useRef } from "react";
import { isTokenValid } from "../../utils/auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../../services/axiosInstance";

export function SessaoCompletaProvider({ children }) {
  return <>{children}</>;
}

const isPosInt = (v) => Number.isInteger(v) && v > 0;

export default function useSessaoCompleta(sessaoIdParam = null) {
  const rqClient = useQueryClient(); 
  
  // 🛡️ BLINDAGEM: Impede o React de afogar a API com chamadas repetidas
  const antiSpamRef = useRef(0);

  const rawToken = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const token = (rawToken && isTokenValid(rawToken)) ? rawToken : null;

  const buscandoPorId = sessaoIdParam !== null && sessaoIdParam !== undefined;
  
  const sessaoIdFromParam = useMemo(() => {
    if (!buscandoPorId) return null;
    const n = Number(sessaoIdParam);
    return isPosInt(n) ? n : null;
  }, [buscandoPorId, sessaoIdParam]);

  // CONFIGURAÇÃO GLOBAL DO CACHE
  const queryConfig = {
    staleTime: 60000,             // 1 minuto de cache intocável
    refetchOnWindowFocus: false,  // Não atualiza só porque o usuário clicou na tela
    refetchOnReconnect: false,    // Não atualiza em microquedas de internet
    retry: 1,                     // Previne loop infinito de erros 500 do backend
  };

  // 1. Query Sessão
  const sessaoAtualRes = useQuery({
    queryKey: ["sessaoAtual", token],
    queryFn: () => axios.get("/sessoes/sessao-atual").then((res) => res.data),
    enabled: !buscandoPorId,
    ...queryConfig
  });

  const sessaoPorIdRes = useQuery({
    queryKey: ["sessaoPorId", sessaoIdFromParam],
    queryFn: () => axios.get(`/sessoes/${sessaoIdFromParam}`).then((res) => res.data),
    enabled: !!sessaoIdFromParam,
    ...queryConfig
  });

  const sessaoData = buscandoPorId ? sessaoPorIdRes.data : sessaoAtualRes.data;
  
  const sessaoId = useMemo(() => {
    if (buscandoPorId) return sessaoIdFromParam;
    const n = Number(sessaoData?.id);
    return isPosInt(n) ? n : null;
  }, [buscandoPorId, sessaoIdFromParam, sessaoData?.id]);

  const hasSession = isPosInt(sessaoId);

  const legislaturaId = useMemo(() => {
    return sessaoData?.legislatura?.id || sessaoData?.legislaturaId || null;
  }, [sessaoData]);

  // --- CONSULTAS COMPLEMENTARES ---

  const expedienteRes = useQuery({
    queryKey: ["expediente", token, sessaoId],
    queryFn: () => axios.get(`/sessao-expediente/${sessaoId}`).then((r) => r.data),
    enabled: hasSession,
    ...queryConfig
  });

  const ordemRes = useQuery({
    queryKey: ["ordem", token, sessaoId],
    queryFn: () => axios.get(`/sessao-ordem/${sessaoId}`).then((r) => r.data),
    enabled: hasSession,
    ...queryConfig
  });

  const tribunaRes = useQuery({
    queryKey: ["tribuna", token, sessaoId],
    queryFn: () => axios.get(`/sessao-tribuna/${sessaoId}`).then((r) => r.data),
    enabled: hasSession,
    ...queryConfig
  });

  const painelRes = useQuery({
    queryKey: ["painel", token, sessaoId],
    queryFn: () => axios.get(`/painel-sessao/${sessaoId}`).then((r) => r.data),
    enabled: hasSession,
    ...queryConfig
  });

const presencasRes = useQuery({
    queryKey: ["presencas_ultimas", token, sessaoId, sessaoData?.faseSessaoId || 1],
    queryFn: () => axios.get(`/presencas/sessao/${sessaoId}/ultimas`, {
      params: { faseSessaoId: sessaoData?.faseSessaoId || 1 } 
    }).then((r) => r.data),
    enabled: hasSession,
    ...queryConfig
  });

  const votacoesRes = useQuery({
    queryKey: ["votacoes", token, sessaoId],
    queryFn: () => axios.get(`/votacoes/sessao/${sessaoId}`).then((r) => r.data),
    enabled: hasSession,
    ...queryConfig
  });

  const pautasRes = useQuery({
    queryKey: ["pautas", token, { page: 1, pageSize: 1000 }],
    queryFn: () => axios.get("/pautas", { params: { page: 1, pageSize: 1000 } }).then((r) => r.data),
    enabled: true,
    staleTime: 1000 * 60 * 5, // 5 minutos blindado
    refetchOnWindowFocus: false,
  });

  const vereadoresRes = useQuery({
    queryKey: ["vereadores_legislatura", token, legislaturaId],
    queryFn: async () => {
        let listaFinal = [];
        if (legislaturaId) {
            try {
                const { data } = await axios.get(`/mandatos/legislatura/${legislaturaId}?ativos=true`);
                if (data && Array.isArray(data)) {
                    listaFinal = data.map((m) => ({ ...m.usuario, mandatoId: m.id, tipoMandato: m.tipo, situacao: m.situacao }));
                }
            } catch (error) { console.warn("⚠️ Falha ao buscar vereadores por legislatura:", error); }
        }
        if (listaFinal.length === 0) {
            try {
                const { data } = await axios.get("/usuarios/vereadores");
                if (Array.isArray(data)) { listaFinal = data.filter(u => u.status === 'ativo'); }
            } catch (err) { console.error("Erro fatal buscando vereadores fallback:", err); }
        }
        return listaFinal;
    },
    enabled: true,
    staleTime: 1000 * 60 * 60, // Blindado por 1 hora
    refetchOnWindowFocus: false,
  });

  // --- ENRIQUECIMENTO E LÓGICA ---

  const { sessaoExpedienteEnriquecido, sessaoOrdemEnriquecida } = useMemo(() => {
    const pautas = pautasRes.data;
    const expediente = expedienteRes.data;
    const ordem = ordemRes.data;
    const listaPautas = Array.isArray(pautas) ? pautas : [];
    const pautasMap = new Map(listaPautas.map(p => [p.id, p]));

    const enrich = (items) => {
      if (!items || !Array.isArray(items)) return [];
      return items.map(item => {
        if (item.pautaId && !item.pauta?.minuta) {
          const pautaCompleta = pautasMap.get(item.pautaId);
          if (pautaCompleta) { return { ...item, pauta: { ...item.pauta, ...pautaCompleta } }; }
        }
        return item;
      });
    };
    return { sessaoExpedienteEnriquecido: enrich(expediente), sessaoOrdemEnriquecida: enrich(ordem) };
  }, [pautasRes.data, expedienteRes.data, ordemRes.data]);

  const pautasDisponiveis = useMemo(() => {
    const pautas = Array.isArray(pautasRes.data) ? pautasRes.data : [];
    const ordem = Array.isArray(ordemRes.data) ? ordemRes.data : [];
    if (!pautas.length) return [];
    return pautas.filter((p) => !ordem.some((o) => o.pautaId === p.id));
  }, [pautasRes.data, ordemRes.data]);

  const loadingSessaoBase = buscandoPorId ? sessaoPorIdRes.isLoading : sessaoAtualRes.isLoading;
  const loading = loadingSessaoBase || [expedienteRes, ordemRes, tribunaRes, painelRes, presencasRes, votacoesRes, vereadoresRes].some(q => q.isLoading);
  
  const errors = [sessaoAtualRes.error, sessaoPorIdRes.error, expedienteRes.error, ordemRes.error, tribunaRes.error, painelRes.error, presencasRes.error, votacoesRes.error, pautasRes.error, vereadoresRes.error].filter(Boolean);
  const error = errors[0] || null;

  const atualizarPresenca = useCallback((userId, presente, motivo) => {
    if (!isPosInt(sessaoId)) return;
    const uid = Number(userId);
    rqClient.setQueryData(["presencas_ultimas", token, sessaoId], (old) => {
      const listaAntiga = Array.isArray(old) ? old : [];
      const getUid = (p) => Number(p?.usuario?.id ?? p?.usuarioId);
      const idx = listaAntiga.findIndex((p) => getUid(p) === uid);
      const now = new Date().toISOString().slice(0, 19).replace("T", " ");
      if (idx > -1) {
        const item = listaAntiga[idx];
        const atualizado = { ...item, presente: presente ? 1 : 0, motivo: motivo ?? item.motivo ?? null, data_criacao: now };
        return [...listaAntiga.slice(0, idx), atualizado, ...listaAntiga.slice(idx + 1)];
      }
      return [...listaAntiga, { id: -Date.now(), sessaoId, usuarioId: uid, usuario: { id: uid }, presente: presente ? 1 : 0, motivo: motivo ?? null, data_criacao: now }];
    });
  }, [rqClient, token, sessaoId]);

const carregarSessaoCompleta = useCallback(() => {
    const now = Date.now();
    if (now - antiSpamRef.current < 300) {
      return Promise.resolve(); 
    }
    antiSpamRef.current = now;

    // Se não tem sessão, invalida as buscas gerais para forçar a descoberta da sessão nova!
    if (!hasSession) { 
        return Promise.all([
            rqClient.invalidateQueries({ queryKey: ["sessaoAtual", token] }),
            rqClient.invalidateQueries({ queryKey: ["sessaoPorId"] })
        ]); 
    }
    
    // Invalida SEMPRE as duas chaves de sessão para não haver incompatibilidade entre componentes
    const work = [
      rqClient.invalidateQueries({ queryKey: ["expediente", token, sessaoId] }),
      rqClient.invalidateQueries({ queryKey: ["ordem", token, sessaoId] }),
      rqClient.invalidateQueries({ queryKey: ["tribuna", token, sessaoId] }),
      rqClient.invalidateQueries({ queryKey: ["painel", token, sessaoId] }),
      rqClient.invalidateQueries({ queryKey: ["presencas_ultimas", token, sessaoId] }),
      rqClient.invalidateQueries({ queryKey: ["votacoes", token, sessaoId] }),
      rqClient.invalidateQueries({ queryKey: ["sessaoAtual", token] }),
      rqClient.invalidateQueries({ queryKey: ["sessaoPorId", sessaoId] }), 
    ];
    return Promise.all(work);
  }, [rqClient, token, sessaoId, hasSession]);

  const returnValue = useMemo(() => ({
    loading,
    error,
    sessaoAtual: sessaoData || null,
    sessaoId,
    legislaturaId, 
    sessaoExpediente: sessaoExpedienteEnriquecido,
    sessaoOrdem: sessaoOrdemEnriquecida,
    sessaoTribuna: Array.isArray(tribunaRes.data) ? tribunaRes.data : [],
    sessaoPainel: painelRes.data?.painel || painelRes.data || [],
    presencas: Array.isArray(presencasRes.data) ? presencasRes.data : [],
    votacoes: Array.isArray(votacoesRes.data) ? votacoesRes.data : [],
    vereadores: Array.isArray(vereadoresRes.data) ? vereadoresRes.data : [], 
    pautasDisponiveis,
    atualizarPresenca,
    carregarSessaoCompleta,
    refetchSessao: buscandoPorId ? sessaoPorIdRes.refetch : sessaoAtualRes.refetch
  }), [
    loading, error, sessaoData, sessaoId, legislaturaId,
    sessaoExpedienteEnriquecido, sessaoOrdemEnriquecida,
    tribunaRes.data, painelRes.data, presencasRes.data, votacoesRes.data,
    vereadoresRes.data, pautasDisponiveis,
    atualizarPresenca, carregarSessaoCompleta,
    buscandoPorId, sessaoPorIdRes.refetch, sessaoAtualRes.refetch
  ]);

  return returnValue;
}